import * as State from './state';
import Event from './event';
import * as Helpers from '../helpers/helpers';
import config from '../configs/config.js';
import AddressConfigModel from '../models/addressConfigModel';

/**
 * Default controller which other controllers extend.
 * Attached methods are available to all controllers.
 */
class AccountController {

    constructor() {
        this.html = '';
    }
    /**
     * Nunjucks generates a template based on data passed in
     * @param {Object} data - Data returned from last step of controller before generateTemplate
     * @param {Object} nunjucks - Template
     * @param {Object} model - Model to be used to populate template
     * @param {Object} viewModel - ViewModel used for viewState conditionals in template
     * @param {string} modelName - Used to determine model name in template
     * @returns {object} A promise containing the template markup
     */
    generateTemplate(data, nunjucks, model, viewModel, modelName, store) {
        const device = this.getDevice(store);
        const groupType = this.cleanGroupType(AddressConfigModel.groupType);

        return new Promise(function(resolve, reject) {
            const template = nunjucks.render({
                [modelName]: model,
                v: viewModel,
                helpers: Helpers,
                locale: store.core.contextPath,
                device: device,
                groupType: groupType,
                currentYear: new Date().getFullYear(),
                state: store,
                core: store.core
            });

            if(typeof template !== 'undefined') {
                resolve(template);
            } else {
                reject(Error('template is busted'));
            }
        });
    }

    getDevice(store) {
        const mobile = store.core.deviceCharacteristics.is_mobile;
        const tablet = store.core.deviceCharacteristics.is_tablet;

        if(tablet) {
            return 'tablet';
        } else if(mobile) {
            return 'mobile';
        } else {
            return 'desktop';
        }
    }

    /**
     * Nunjucks generates a template based on data passed in
     * @param {Object} nunjucks - Template
     * @return {object} A promise containing the template markup
     */
    generateHeader(nunjucks) {
        return new Promise(function(resolve, reject) {
            const template = nunjucks.render({});
            resolve(template);
        });
    }
    /**
     * Attach html generated from nunjucks template to the page
     * @param {string} data - Template string passed to the state controller to be converted to vdom
     * @return {object} A promise containing the vDOM rootNode object
     */
    attachTemplateToDom(data) {
        return State.updateView(data);
    }
    /**
     * Attach html generated from nunjucks template to the header
     * @param {string} data - Template string passed to the state controller to be converted to vdom
     * @return {object} A promise containing the vDOM rootNode object
     */
    attachHeaderToDom(data) {
        return State.updateHeader(data);
    }
    /**
     * Get data from specified model
     * @param {Object} model - Model whose properties will be retrieved
     * @param {string} method - Method used to call service at specific endpoint
     * @param {Object} token - Auth token
     * @param {string} modelProperty - Property of current model which will be updated
     * @return {object} The model data
     */
    get(model, method, token, modelProperty, viewModel = {}) {
        if(Object.keys(model.initialized[modelProperty]).length === 0 || model.initialized[modelProperty].serviceRan === false){
            return model.getData(method, token, model, viewModel);
        } else {
            // data has already been retrieved and set
            return new Promise(function(resolve, reject) {
                resolve(true);
            });
        }

    }
    /**
     * Set specified model property to equal serviceResponse
     * @param {Object} model - Model whose properties will be mutated
     * @param {Object} serviceResponse - The response object returned from the service, containing status and data
     * @param {string} prop - The property of the model to update
     * @param {boolean} multipleValues - Used to determine whether to dump all of data into the property
     * @param {boolean} viewModel - the current viewModel
     * @return {object} The updated model
     */
    set(model, serviceResponse, prop, multipleValues, viewModel = {}) {
        // returned as a promise for the controller promise chain
        return new Promise(function(resolve, reject) {
            if(typeof viewModel !== 'undefined' && typeof viewModel.viewStates !== 'undefined' && typeof viewModel.viewStates.callSubscriptions !== 'undefined' && viewModel.viewStates.callSubscriptions === true) {
                viewModel.viewStates.callSubscriptions = false;
            }

            if((typeof serviceResponse !== 'undefined' && typeof serviceResponse.status !== 'undefined' && serviceResponse.status == 200) || (typeof serviceResponse !== 'undefined' && typeof serviceResponse.nonApiResponse !== 'undefined' && serviceResponse.nonApiResponse === true)) {
                // check that model exists and if data equals false (the return from a 404)
                if(typeof model !== 'undefined') {
                    model.setInitialized(prop, true, false);
                    // Eventually temp will be set after successful GETs2q1
                    // let tempName = 'temp'+prop.charAt(0).toUpperCase()+prop.slice(1);
                    // model.setData(serviceResponse.data,tempName,multipleValues);

                    resolve(model.setData(serviceResponse.data, prop, multipleValues));

                } else {
                    reject(Error('No profile model found to set'));
                }
            } else if(typeof serviceResponse !== 'undefined' && typeof serviceResponse.status !== 'undefined' && (serviceResponse.status == 404 || serviceResponse.status.code == 404)) {
                resolve(model.setInitialized(prop, true, true));
            } else if(config.defaults.useMockData) {
                if(typeof model !== 'undefined') {
                    model.setInitialized(prop, true, false);
                    resolve(model.setData(serviceResponse, prop, multipleValues));
                } else {
                    reject(Error('No profile model found to set'));
                }
            } else {
                resolve(model);
            }
        });
    }

    /**
     * Attach events from the viewModel
     * @param {Object} viewModel - ViewModel associated with current controller
     * @param {Object} controller - Controller for current page
     * @param {Object} model - Model associated with current controller
     * @param {Object} token - Auth token
     * @returns {object} Event object
     */
    attachEvents(viewModel, controller, model, token, store) {
        return Event.attachEvents(viewModel, controller, model, token, store);
    }

    /**
     * checks for the presence of `fp` groupType designation and removes it
     * @param  {string} groupType the groupType value returned by the service
     * @return {string}           the cleaned groupType value
     */
    cleanGroupType(groupType) {
        if(typeof groupType !== 'string' || groupType === undefined || groupType === null) {
            return groupType;
        }
        return groupType.replace('fp', '');
    }
}

export default AccountController;
